import React from 'react';
import { usePS2Graphics } from '../context/PS2GraphicsContext';
import { CountryClubCrest, Ps2ControllerIcon, Ps2MemoryCardIcon, NetworkAdapterIcon } from './Icons';

export default function Footer() {
  const { ps2Mode } = usePS2Graphics();

  return (
    <footer role="contentinfo" aria-label="Site footer" className={`relative backdrop-blur-xl text-white transition-colors ${
      ps2Mode
        ? 'bg-gradient-to-b from-slate-950/95 via-slate-950 to-black'
        : 'bg-gradient-to-b from-black/90 via-black to-black'
    }`}>
      {/* Top gradient divider */}
      <div className={`absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent ${
        ps2Mode ? 'via-cyan-500/30' : 'via-emerald-500/20'
      } to-transparent`} />

      {/* Divider */}
      <div className="border-t border-white/[0.04]" aria-hidden="true">
        <div className="flex justify-center py-5">
          <div className={`font-mono text-lg font-semibold flex items-center gap-3 ${
            ps2Mode ? 'text-cyan-500/20' : 'text-white/15'
          }`}>
            <span>─</span>
            <CountryClubCrest className={`w-5 h-5 ${ps2Mode ? 'text-cyan-500/30' : 'text-emerald-500/30'}`} />
            <span>─</span>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-8 py-12 md:py-16">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-14 mb-10">
          {/* About */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                ps2Mode
                  ? 'bg-cyan-500/10 border border-cyan-500/20'
                  : 'bg-emerald-500/10 border border-emerald-500/20'
              }`}>
                <CountryClubCrest className={`w-5 h-5 ${ps2Mode ? 'text-cyan-400' : 'text-emerald-400'}`} />
              </div>
              <h3 className="text-white font-bold text-xl tracking-tight">
                The Country<span className={ps2Mode ? 'text-cyan-400' : 'text-emerald-400'}>Club</span>
              </h3>
            </div>
            <p className="text-white/60 text-sm leading-relaxed font-light">
              A revival gaming community where the greens are pixelated, the food is microwaved, and the PS2 never sleeps. Pull up a chair.
            </p>

            {/* PS2 System Info Badge */}
            {ps2Mode && (
              <div className="mt-4 p-3 rounded-lg bg-cyan-500/5 border border-cyan-500/15">
                <div className="flex items-center gap-2 mb-2">
                  <Ps2ControllerIcon className="w-4 h-4 text-cyan-400" />
                  <span className="text-[10px] font-mono font-bold text-cyan-300 tracking-wider uppercase">System Info</span>
                </div>
                <div className="space-y-1 text-[10px] font-mono text-cyan-200/60">
                  <div className="flex justify-between">
                    <span>Emotion Engine</span>
                    <span className="text-cyan-400">128-bit</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Memory Card</span>
                    <span className="text-cyan-400">8MB (PS2)</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Network Adapter</span>
                    <span className="text-emerald-400">Connected</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div>
            <h4 className={`font-bold mb-5 text-xs tracking-[0.15em] uppercase ${
              ps2Mode ? 'text-cyan-300' : 'text-white'
            }`}>Resources</h4>
            <ul className="text-white/60 text-sm space-y-2.5">
              <li>
                <a href="https://cloud.socom.gg/s/ZnXtxY3DM8JfxGY" target="_blank" rel="noopener noreferrer" className={`transition-colors duration-300 inline-flex items-center gap-1.5 ${
                  ps2Mode ? 'hover:text-cyan-300' : 'hover:text-white'
                }`}>
                  Emulator Pack
                  <svg className="w-3 h-3 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                </a>
              </li>
              <li>
                <a href="https://socom.gg/" target="_blank" rel="noopener noreferrer" className={`transition-colors duration-300 inline-flex items-center gap-1.5 ${
                  ps2Mode ? 'hover:text-cyan-300' : 'hover:text-white'
                }`}>
                  SOCOM.gg
                  <svg className="w-3 h-3 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </li>
            </ul>
          </div>

          {/* Community */}
          <div>
            <h4 className={`font-bold mb-5 text-xs tracking-[0.15em] uppercase ${
              ps2Mode ? 'text-cyan-300' : 'text-white'
            }`}>Community</h4>
            <div className="space-y-2.5">
              <a
                href="https://discord.gg/DQhhgYFH8"
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-3 text-white/60 transition-colors duration-300 text-sm ${
                  ps2Mode ? 'hover:text-cyan-300' : 'hover:text-white'
                }`}
                aria-label="Discord"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20.317 4.369A19.791 19.791 0 0 0 16.885 3.3a.077.077 0 0 0-.082.038c-.357.63-.755 1.453-1.037 2.104a18.524 18.524 0 0 0-5.532 0 12.683 12.683 0 0 0-1.04-2.104.077.077 0 0 0-.082-.038A19.736 19.736 0 0 0 3.684 4.369a.07.07 0 0 0-.032.027C.533 9.09-.32 13.579.099 18.021a.082.082 0 0 0 .031.056c2.128 1.565 4.195 2.51 6.229 3.155a.077.077 0 0 0 .084-.027c.48-.66.908-1.356 1.273-2.084a.076.076 0 0 0-.041-.104c-.676-.256-1.32-.568-1.938-.936a.077.077 0 0 1-.008-.127c.13-.098.26-.2.384-.304a.074.074 0 0 1 .077-.01c4.06 1.855 8.447 1.855 12.47 0a.075.075 0 0 1 .078.009c.124.104.254.206.384.304a.077.077 0 0 1-.006.127 12.298 12.298 0 0 1-1.94.936.076.076 0 0 0-.04.105c.366.728.794 1.424 1.272 2.084a.076.076 0 0 0 .084.028c2.035-.645 4.102-1.59 6.23-3.155a.077.077 0 0 0 .03-.055c.5-5.177-.838-9.637-3.548-13.625a.061.061 0 0 0-.03-.028ZM8.02 15.331c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.418 2.157-2.418 1.21 0 2.175 1.094 2.157 2.418 0 1.334-.955 2.419-2.157 2.419Zm7.974 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.418 2.157-2.418 1.21 0 2.175 1.094 2.157 2.418 0 1.334-.947 2.419-2.157 2.419Z" fill="currentColor"/>
                </svg>
                <span>Discord Server</span>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className={`border-t pt-8 ${ps2Mode ? 'border-cyan-500/10' : 'border-white/[0.06]'}`}>
          <div className="flex flex-col md:flex-row justify-between items-center text-xs text-white/40 font-mono gap-4 mb-3">
            <p>© 2026 The Country Club. No actual golf required.</p>
            <div className="flex items-center gap-5">
              {ps2Mode && (
                <span className="inline-flex items-center gap-1.5 text-cyan-400/50">
                  <Ps2MemoryCardIcon className="w-3.5 h-3.5" />
                  <span>Maintained by The Country Club</span>
                </span>
              )}
              <span className="text-white/30">Powered by Socom.GG</span>
            </div>
          </div>
          <p className="text-[10px] text-white/30 text-center font-mono leading-relaxed">
            Not affiliated with Sony Interactive Entertainment. PlayStation is a registered trademark. All rights reserved to their respective owners.
          </p>
        </div>
      </div>
    </footer>
  );
}
